import React, { useState, useEffect, useRef } from 'react';
import { OnboardingFormData } from '../../types';
import authService from '../../services/authService';
import voiceService from '../../services/voiceService';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Building, MapPin, User, Mail, Phone, ArrowLeft, ArrowRight, CheckCircle2, AlertCircle, Globe } from "lucide-react";
import { cn } from "@/lib/utils";

/** E-Mail: mindestens local@domain.tld */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** Telefon: nur Ziffern und +, mind. 6 Ziffern (nach Bereinigung) */
function validatePhone(phone: string): { valid: boolean; message?: string } {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 6) return { valid: false, message: 'Telefonnummer zu kurz (mind. 6 Ziffern).' };
  if (digits.length > 15) return { valid: false, message: 'Telefonnummer zu lang.' };
  if (!/^\+?[\d\s\-/()]+$/.test(phone.trim())) return { valid: false, message: 'Telefonnummer enthält ungültige Zeichen.' };
  return { valid: true };
}
/** Umsatzsteuer-ID: DE + 9 Ziffern oder anderes EU-Format (2 Buchstaben + 2–12 Zeichen) */
function validateVatId(vatId: string): { valid: boolean; message?: string } {
  const raw = (vatId || '').trim().toUpperCase().replace(/\s/g, '');
  if (raw.length < 4) return { valid: false, message: 'Umsatzsteuer-ID eingeben (z. B. DE123456789).' };
  if (/^DE\d{9}$/.test(raw)) return { valid: true };
  if (/^[A-Z]{2}[A-Z0-9]{2,12}$/.test(raw)) return { valid: true };
  return { valid: false, message: 'Umsatzsteuer-ID: 2 Buchstaben (Ländercode) + Ziffern/Buchstaben (z. B. DE123456789).' };
}
/** URL: muss mit https? beginnen und Punkt in Domain haben */
function validateUrl(url: string): { valid: boolean; message?: string } {
  const raw = (url || '').trim();
  if (!raw) return { valid: false, message: 'Bitte URL angeben.' };
  try {
    const u = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
    if (!u.hostname.includes('.')) return { valid: false, message: 'Ungültige URL (Domain erforderlich).' };
    return { valid: true };
  } catch {
    return { valid: false, message: 'Ungültige URL (z. B. https://ihre-seite.de/datenschutz).' };
  }
}

interface CompanyInfoStepProps {
  formData: OnboardingFormData;
  onFormChange: (data: Partial<OnboardingFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const CompanyInfoStep: React.FC<CompanyInfoStepProps> = ({ formData, onFormChange, onNext, onBack }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dataFetched, setDataFetched] = useState<boolean>(false);
  
  const onFormChangeRef = useRef(onFormChange);
  
  useEffect(() => {
    onFormChangeRef.current = onFormChange;
  }, [onFormChange]);
  
  useEffect(() => {
    if (dataFetched) return;
    
    const fetchBusinessInfo = async () => {
      try {
        setIsLoading(true);
        
        // Fetch business info and voice settings in parallel
        const [businessInfo, voiceSettings] = await Promise.all([
          authService.getBusinessInfo(),
          voiceService.getSettings().catch(() => ({ privacy_policy_url: '' }))
        ]);
        
        const mappedData: Partial<OnboardingFormData> = {
          companyName: businessInfo.business_name || '',
          vatId: businessInfo.tax_id || '',
          street: businessInfo.street_address || '',
          zipCode: businessInfo.postal_code || '',
          city: businessInfo.city || '',
          country: businessInfo.country || '',
          contactPerson: businessInfo.contact_person || '',
          email: businessInfo.email || '',
          phone: businessInfo.phone_number || '',
          privacyPolicyUrl: voiceSettings.privacy_policy_url || ''
        };
        
        onFormChangeRef.current(mappedData);
        setDataFetched(true);
        setError(null);
      } catch (err) {
        console.error('Error fetching business info:', err);
        if (err instanceof Error && !err.message.includes('No authentication token found')) {
          setError('Fehler beim Laden der Unternehmensinformationen.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchBusinessInfo();
  }, [dataFetched]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    onFormChange({ [name]: e.target.value });
    setSuccess(null);
    setError(null);
    if (fieldError === name) setFieldError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldError(null);

    const trim = (s: string | undefined) => (s || '').trim();
    if (!trim(formData.companyName)) { setFieldError('companyName'); return; }
    if (!trim(formData.street)) { setFieldError('street'); return; }
    if (!trim(formData.zipCode)) { setFieldError('zipCode'); return; }
    if (!trim(formData.city)) { setFieldError('city'); return; }
    if (!trim(formData.country)) { setFieldError('country'); return; }
    if (!trim(formData.contactPerson)) { setFieldError('contactPerson'); return; }

    if (!trim(formData.email)) { setFieldError('email'); return; }
    if (!EMAIL_REGEX.test(formData.email.trim())) { setFieldError('email'); return; }

    if (!trim(formData.phone)) { setFieldError('phone'); return; }
    const phoneCheck = validatePhone(formData.phone);
    if (!phoneCheck.valid) { setFieldError('phone'); return; }

    if (!trim(formData.vatId)) { setFieldError('vatId'); return; }
    const vatCheck = validateVatId(formData.vatId);
    if (!vatCheck.valid) { setFieldError('vatId'); return; }

    if (!trim(formData.privacyPolicyUrl)) { setFieldError('privacyPolicyUrl'); return; }
    const urlCheck = validateUrl(formData.privacyPolicyUrl);
    if (!urlCheck.valid) { setFieldError('privacyPolicyUrl'); return; }
    
    try {
      setIsSaving(true);
      setFieldError(null);
      
      const businessInfoData = {
        business_name: formData.companyName,
        tax_id: formData.vatId,
        street_address: formData.street,
        postal_code: formData.zipCode,
        city: formData.city,
        country: formData.country,
        contact_person: formData.contactPerson,
        email: formData.email,
        phone_number: formData.phone
      };
      
      // Save business info and privacy policy URL in parallel
      await Promise.all([
        authService.updateBusinessInfo(businessInfoData),
        formData.privacyPolicyUrl 
          ? voiceService.updatePrivacyUrl(formData.privacyPolicyUrl)
          : Promise.resolve()
      ]);
      
      setSuccess('Unternehmensdaten erfolgreich gespeichert.');
      
      setTimeout(() => {
        onNext();
      }, 1000);
    } catch (err) {
      console.error('Error updating business info:', err);
      setError('Fehler beim Speichern der Daten.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary-strong" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {success && (
        <Alert className="bg-success/10 border-success/25 text-success-strong">
          <CheckCircle2 className="h-4 w-4 text-success-strong" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}
      
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {fieldError && !error && (
        <p className="text-sm text-warning-strong">
          Bitte prüfen Sie die markierten Felder.
        </p>
      )}
    
      <div className="space-y-2">
        <h2 className="flex items-center text-xl font-semibold text-foreground">
          <Building className="mr-2 h-6 w-6 text-[#1967D2]" />
          Unternehmensinformationen
        </h2>
        <p className="text-muted-foreground text-sm">
          Bitte geben Sie die Informationen zu Ihrem Unternehmen ein.
        </p>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="companyName">Unternehmensname <span className="text-destructive-strong">*</span></Label>
          <div className="relative">
            <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              required
              className={cn("pl-9 h-11", fieldError === 'companyName' && "border-destructive focus-visible:ring-destructive")}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="vatId">Umsatzsteuer-ID <span className="text-destructive-strong">*</span></Label>
          <div className="relative">
            <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="vatId"
              name="vatId"
              value={formData.vatId || ''}
              onChange={handleInputChange}
              required
              className={cn("pl-9 h-11", fieldError === 'vatId' && "border-destructive focus-visible:ring-destructive")}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="street">Straße und Hausnummer <span className="text-destructive-strong">*</span></Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="street"
              name="street"
              value={formData.street}
              onChange={handleInputChange}
              required
              className={cn("pl-9 h-11", fieldError === 'street' && "border-destructive focus-visible:ring-destructive")}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="zipCode">Postleitzahl <span className="text-destructive-strong">*</span></Label>
            <Input
              id="zipCode"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleInputChange}
              required
              className={cn("h-11", fieldError === 'zipCode' && "border-destructive focus-visible:ring-destructive")}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="city">Stadt <span className="text-destructive-strong">*</span></Label>
            <Input
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              required
              className={cn("h-11", fieldError === 'city' && "border-destructive focus-visible:ring-destructive")}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="country">Land <span className="text-destructive-strong">*</span></Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              required
              className={cn("pl-9 h-11", fieldError === 'country' && "border-destructive focus-visible:ring-destructive")}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="contactPerson">Ansprechpartner <span className="text-destructive-strong">*</span></Label>
          <div className="relative">
            <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="contactPerson"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleInputChange}
              required
              className={cn("pl-9 h-11", fieldError === 'contactPerson' && "border-destructive focus-visible:ring-destructive")}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail <span className="text-destructive-strong">*</span></Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className={cn("pl-9 h-11", fieldError === 'email' && "border-destructive focus-visible:ring-destructive")}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone">Salon-Telefonnummer <span className="text-destructive-strong">*</span></Label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                placeholder="+49 …"
                className={cn("pl-9 h-11", fieldError === 'phone' && "border-destructive focus-visible:ring-destructive")}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Hier landet ein Anrufer, wenn der Telefon-Agent ihn an einen Mitarbeiter weiterleitet (z.B. Salon-Festnetz).
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="privacyPolicyUrl">Datenschutz-Seite (URL) <span className="text-destructive-strong">*</span></Label>
          <div className="relative">
            <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="privacyPolicyUrl"
              name="privacyPolicyUrl"
              type="url"
              placeholder="https://ihre-website.de/datenschutz"
              value={formData.privacyPolicyUrl || ''}
              onChange={handleInputChange}
              required
              className={cn("pl-9 h-11", fieldError === 'privacyPolicyUrl' && "border-destructive focus-visible:ring-destructive")}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Link zu Ihrer Datenschutzerklärung. Wird vom WhatsApp-Agenten und Telefonagenten verwendet.
          </p>
        </div>
      </div>
      
      <div className="flex justify-between pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={isLoading || isSaving}
          className="px-6 h-11 border-border"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Zurück
        </Button>
        <Button
          type="submit"
          disabled={isLoading || isSaving}
          className="bg-[#1967D2] hover:bg-[#1756B6] text-white px-8 h-11"
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Speichern...
            </>
          ) : (
            <>
              Weiter <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default CompanyInfoStep;
